import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  People as PeopleIcon,
  FilterList as FilterIcon,
  Menu as MenuIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import PublicProfileCard from '../components/Profile/PublicProfileCard';
import { PublicProfile, PublicProfilesService } from '../services/publicProfilesService';

const MembersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchProfiles = async (q?: string) => {
    try {
      setLoading(true);
      setError(null);
      const rows = await PublicProfilesService.getPublicProfiles({ search: q || undefined, limit: 80 });
      setProfiles(rows);
    } catch (e: any) {
      console.error('Failed to load public profiles', e);
      setError(e?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = String(query || '').trim();
      const urlQ = String(searchParams.get('search') || '').trim();
      if (q !== urlQ) {
        if (q) setSearchParams({ search: q }, { replace: true });
        else setSearchParams({}, { replace: true });
      }
      void fetchProfiles(q);
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const initial = String(searchParams.get('search') || '').trim();
    setQuery(initial);
    void fetchProfiles(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sidebar = useMemo(() => {
    return (
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(99,102,241,1) 0%, rgba(14,165,233,1) 100%)',
              color: 'white',
              boxShadow: '0 12px 28px rgba(99,102,241,0.22)',
            }}
          >
            <PeopleIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>Members</Typography>
            <Typography variant="body2" color="text.secondary">
              Public profiles directory
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gap: 1.25 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<FilterIcon />}
            sx={{
              justifyContent: 'flex-start',
              borderRadius: 3,
              px: 2,
              fontWeight: 900,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 10px 24px rgba(102,126,234,0.24)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7286ff 0%, #8357b2 100%)',
                boxShadow: '0 14px 30px rgba(102,126,234,0.30)',
              },
            }}
          >
            All public members
          </Button>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.06)',
              bgcolor: 'rgba(255,255,255,0.72)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Tips</Typography>
            <Typography variant="body2" color="text.secondary">
              Search by name, alias, bio, or location.
            </Typography>
          </Paper>
        </Box>
      </Box>
    );
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Box
          sx={{
            mb: 3,
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.06)',
            background:
              'radial-gradient(900px 260px at 10% 10%, rgba(99,102,241,0.18), transparent), radial-gradient(700px 240px at 80% 0%, rgba(14,165,233,0.16), transparent), linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.92) 100%)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.06, mb: 0.75 }}>
                Mitglieder
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Entdecke öffentliche Profile in einem schönen Verzeichnis.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  display: { xs: 'inline-flex', md: 'none' },
                  border: '1px solid rgba(0,0,0,0.08)',
                  bgcolor: 'rgba(255,255,255,0.7)',
                }}
                aria-label="open navigation"
              >
                <MenuIcon />
              </IconButton>
              <IconButton
                onClick={() => void fetchProfiles(query)}
                sx={{
                  border: '1px solid rgba(0,0,0,0.08)',
                  bgcolor: 'rgba(255,255,255,0.7)',
                }}
                aria-label="refresh"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' }, gap: 2 }}>
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Mitglieder suchen…"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  bgcolor: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(8px)',
                },
              }}
            />

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 4,
                border: '1px solid rgba(0,0,0,0.06)',
                bgcolor: 'rgba(255,255,255,0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography color="text.secondary" variant="body2">
                {loading ? 'Loading…' : `${profiles.length} Profile`}
              </Typography>
              <PeopleIcon sx={{ color: 'rgba(99,102,241,1)' }} />
            </Paper>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 3, alignItems: 'start' }}>
          {/* Desktop sidebar */}
          <Paper
            elevation={0}
            sx={{
              display: { xs: 'none', md: 'block' },
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.06)',
              bgcolor: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(12px)',
              position: 'sticky',
              top: 92,
              overflow: 'hidden',
            }}
          >
            {sidebar}
          </Paper>

          {/* Mobile sidebar drawer */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: 320 } }}
          >
            {sidebar}
          </Drawer>

          {/* Content */}
          <Box>
            {error ? (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid rgba(239,68,68,0.25)', bgcolor: 'rgba(254,242,242,0.7)' }}>
                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Something went wrong</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {error}
                </Typography>
                <Button variant="contained" onClick={() => void fetchProfiles(query)}>
                  Try again
                </Button>
              </Paper>
            ) : loading ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 2.5,
                }}
              >
                {profiles.map((p) => (
                  <PublicProfileCard key={p.id} profile={p} />
                ))}

                {profiles.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      gridColumn: '1 / -1',
                      p: 4,
                      borderRadius: 4,
                      border: '1px dashed rgba(0,0,0,0.18)',
                      bgcolor: 'rgba(255,255,255,0.65)',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                      Keine Profile gefunden
                    </Typography>
                    <Typography color="text.secondary">
                      Versuche einen anderen Suchbegriff.
                    </Typography>
                  </Paper>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MembersPage;
